import { Helmet } from "react-helmet-async";
import { InsightsNavbar } from "@/components/InsightsNavbar";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Cookie, BarChart3, Lock, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = "January 26, 2025";

  return (
    <>
      <Helmet>
        <title>Privacy Policy | IT Dukes Insights</title>
        <meta
          name="description"
          content="Learn how IT Dukes Insights collects, uses, and protects your data. Our privacy policy covers Google Analytics usage, cookies, and data handling practices."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://insights.itdukes.com/privacy-policy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <InsightsNavbar />

        <main className="page-container py-12 sm:py-16">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                  Privacy Policy
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Last updated: {lastUpdated}
                </p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Your privacy is important to us. This policy explains how IT Dukes Insights 
              collects, uses, and protects your information when you use our website and tools.
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-slate dark:prose-invert max-w-3xl space-y-10">
            {/* Section 1: Information We Collect */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-semibold text-foreground m-0">
                  Information We Collect
                </h2>
              </div>
              <div className="bg-muted/30 border border-border rounded-xl p-6 space-y-4">
                <div>
                  <h3 className="font-medium text-foreground mb-2">Analytics Data</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    We use Google Analytics (GA4) to understand how visitors interact with our website. 
                    This service collects anonymized data including:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Pages visited and time spent on each page</li>
                    <li>• Tools used and features accessed</li>
                    <li>• Device type, browser, and operating system</li>
                    <li>• Approximate geographic location (country/city level)</li>
                    <li>• Referral sources (how you found us)</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium text-foreground mb-2">Information You Provide</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    When you use certain features or tools, you may voluntarily provide:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Name and email address (for tool access or newsletter signup)</li>
                    <li>• Domain names or email addresses you want to analyze</li>
                    <li>• Feedback or issue reports you submit</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 2: Cookies */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Cookie className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-semibold text-foreground m-0">
                  Cookies & Tracking Technologies
                </h2>
              </div>
              <div className="bg-muted/30 border border-border rounded-xl p-6 space-y-4">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Our website uses cookies and similar technologies to enhance your experience 
                  and collect analytics data. Here's what we use:
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 font-medium text-foreground">Cookie</th>
                        <th className="text-left py-2 pr-4 font-medium text-foreground">Provider</th>
                        <th className="text-left py-2 pr-4 font-medium text-foreground">Purpose</th>
                        <th className="text-left py-2 font-medium text-foreground">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b border-border/50">
                        <td className="py-2 pr-4 font-mono text-xs">_ga</td>
                        <td className="py-2 pr-4">Google Analytics</td>
                        <td className="py-2 pr-4">Distinguishes unique users</td>
                        <td className="py-2">2 years</td>
                      </tr>
                      <tr className="border-b border-border/50">
                        <td className="py-2 pr-4 font-mono text-xs">_ga_*</td>
                        <td className="py-2 pr-4">Google Analytics</td>
                        <td className="py-2 pr-4">Persists session state</td>
                        <td className="py-2">2 years</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4 font-mono text-xs">tool_session</td>
                        <td className="py-2 pr-4">IT Dukes</td>
                        <td className="py-2 pr-4">Maintains tool access session</td>
                        <td className="py-2">Session</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <p className="text-muted-foreground text-sm leading-relaxed">
                  You can control cookies through your browser settings. However, disabling cookies 
                  may affect functionality of some features.
                </p>
              </div>
            </section>

            {/* Section 3: How We Use Your Information */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-semibold text-foreground m-0">
                  How We Use Your Information
                </h2>
              </div>
              <div className="bg-muted/30 border border-border rounded-xl p-6">
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    Provide and improve our tools and services
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    Analyze usage patterns to enhance user experience
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    Send relevant updates if you've subscribed to our communications
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    Respond to your feedback and support requests
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    Detect and prevent fraudulent or abusive activity
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4: Data Sharing */}
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                Data Sharing & Third Parties
              </h2>
              <div className="bg-muted/30 border border-border rounded-xl p-6 space-y-4">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  We do not sell your personal information. We may share data with:
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground min-w-[120px]">Google Analytics:</span>
                    <span>Anonymized usage data for analytics purposes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground min-w-[120px]">Service Providers:</span>
                    <span>Third-party services that help us operate (e.g., hosting, email)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground min-w-[120px]">Legal Requirements:</span>
                    <span>When required by law or to protect our rights</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5: Data Retention */}
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                Data Retention
              </h2>
              <div className="bg-muted/30 border border-border rounded-xl p-6">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Analytics data is retained according to Google Analytics' default retention period (14 months). 
                  Personal information you provide (such as email addresses for tool access) is retained 
                  as long as necessary to provide our services or as required by law. You can request 
                  deletion of your data at any time.
                </p>
              </div>
            </section>

            {/* Section 6: Your Rights */}
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                Your Rights
              </h2>
              <div className="bg-muted/30 border border-border rounded-xl p-6">
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  Depending on your location, you may have rights including:
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Access to your personal data</li>
                  <li>• Correction of inaccurate data</li>
                  <li>• Deletion of your data</li>
                  <li>• Opt-out of marketing communications</li>
                  <li>• Data portability</li>
                </ul>
              </div>
            </section>

            {/* Section 7: Opt-Out */}
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                Opting Out of Analytics
              </h2>
              <div className="bg-muted/30 border border-border rounded-xl p-6">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  You can opt out of Google Analytics tracking by:
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li>
                    • Installing the{" "}
                    <a
                      href="https://tools.google.com/dlpage/gaoptout"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Google Analytics Opt-out Browser Add-on
                    </a>
                  </li>
                  <li>• Adjusting your browser's cookie settings</li>
                  <li>• Using browser privacy/incognito mode</li>
                </ul>
              </div>
            </section>

            {/* Section 8: Contact */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-semibold text-foreground m-0">
                  Contact Us
                </h2>
              </div>
              <div className="bg-muted/30 border border-border rounded-xl p-6">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  If you have questions about this privacy policy or your data, please contact us at{" "}
                  <a
                    href="mailto:info@itdukes.com"
                    className="text-primary hover:underline"
                  >
                    info@itdukes.com
                  </a>
                  {" "}or visit{" "}
                  <a
                    href="https://itdukes.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    itdukes.com
                  </a>
                  .
                </p>
              </div>
            </section>

            {/* Section 9: Changes */}
            <section>
              <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                Changes to This Policy
              </h2>
              <div className="bg-muted/30 border border-border rounded-xl p-6">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  We may update this privacy policy from time to time. We will notify you of any 
                  changes by posting the new policy on this page and updating the "Last updated" date.
                </p>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
